import { GoogleGenAI } from '@google/genai';
import { Client, RenewalPrediction, SatisfactionLevel, TaskStatus, OnboardingTask } from '../types';

// Use the environment variable as configured in vite.config.ts
const GEMINI_API_KEY = process.env.GEMINI_API_KEY || process.env.API_KEY;

if (!GEMINI_API_KEY) {
  throw new Error("GEMINI_API_KEY environment variable not set for Gemini API. Please check your environment configuration.");
}

const ai = new GoogleGenAI({ apiKey: GEMINI_API_KEY });

// Rate limiting configuration
const RATE_LIMIT_CONFIG = {
  maxRequestsPerMinute: 10, // Conservative limit for free tier
  maxRequestsPerDay: 50,    // Conservative daily limit
  requestDelayMs: 6000,     // 6 seconds between requests
};

// Request tracking
let requestCount = 0;
let dailyRequestCount = 0;
let lastRequestTime = 0;
let dailyResetTime = 0;

// Reset daily counter at midnight Pacific time
const resetDailyCounter = () => {
  const now = Date.now();
  const pacificTime = new Date().toLocaleString("en-US", { timeZone: "America/Los_Angeles" });
  const todayPacific = new Date(pacificTime).setHours(0, 0, 0, 0);
  
  if (now >= dailyResetTime) {
    dailyResetTime = todayPacific + 24 * 60 * 60 * 1000; // Next midnight Pacific
    dailyRequestCount = 0;
  }
};

// Rate limiting function
const checkRateLimit = async (): Promise<void> => {
  const now = Date.now();
  
  // Reset daily counter if needed
  resetDailyCounter();
  
  // Check daily limit
  if (dailyRequestCount >= RATE_LIMIT_CONFIG.maxRequestsPerDay) {
    throw new Error(`Daily API quota exceeded (${RATE_LIMIT_CONFIG.maxRequestsPerDay} requests). Please wait until tomorrow or upgrade to a paid tier.`);
  }
  
  // Check time-based rate limiting
  const timeSinceLastRequest = now - lastRequestTime;
  if (timeSinceLastRequest < RATE_LIMIT_CONFIG.requestDelayMs) {
    const waitTime = RATE_LIMIT_CONFIG.requestDelayMs - timeSinceLastRequest;
    console.log(`Rate limiting: waiting ${waitTime}ms before next request`);
    await new Promise(resolve => setTimeout(resolve, waitTime));
  }
  
  // Update counters
  requestCount++;
  dailyRequestCount++;
  lastRequestTime = Date.now();
};

// Enhanced error handling
const handleGeminiError = (error: any): string => {
  console.error('Gemini API Error:', error);
  
  // Check for specific error types
  if (error?.message?.includes('429') || error?.status === 429) {
    return 'Rate limit exceeded. Please wait a few minutes before trying again.';
  }
  
  if (error?.message?.includes('quota') || error?.message?.includes('QUOTA_EXCEEDED')) {
    return 'Daily quota exceeded. Please wait until tomorrow or upgrade to a paid tier. Daily quotas reset at midnight Pacific time.';
  }
  
  if (error?.message?.includes('403') || error?.status === 403) {
    return 'Access denied. Please check your API key permissions or try again later.';
  }
  
  if (error?.message?.includes('400') || error?.status === 400) {
    return 'Invalid request. Please check your input and try again.';
  }
  
  if (error?.message?.includes('SAFETY') || error?.message?.includes('BLOCKED')) {
    return 'Content was blocked due to safety settings. Please try rephrasing your request.';
  }
  
  return 'AI service temporarily unavailable. Please try again later.';
};

const getTaskStatusText = (task: OnboardingTask) => {
    const scheduleText = task.scheduledDate ? ` (Scheduled: ${new Date(task.scheduledDate).toLocaleDateString()})` : '';
    if (task.status === TaskStatus.Reported) return `Awaiting Approval${scheduleText}`;
    if (task.status === TaskStatus.Approved) return 'Completed';
    return `Pending${scheduleText}`;
};
  
const getServiceStatusText = (hasService: boolean, task: OnboardingTask) => {
    if (!hasService) return 'Not Required';
    return getTaskStatusText(task);
};

const getLatestRemarkText = (task: OnboardingTask) => {
    if (!task.remarks || task.remarks.length === 0) return 'No remarks';
    return task.remarks[task.remarks.length - 1];
};

const getFinancialSummary = (client: Client) => {
    const parts = [];
    if (client.totalPrice) parts.push(`Total: د.إ${client.totalPrice}`);
    if (client.paymentType) parts.push(`Payment: ${client.paymentType}`);
    if (client.isPaymentPending) parts.push(`Pending: د.إ${client.pendingAmount || 0}`);
    return parts.join(', ') || 'No financial data';
};

export const generateRemarksSuggestion = async (client: Client): Promise<string> => {
  try {
    // Check rate limits before making request
    await checkRateLimit();
    
    const { name, company, email, numberOfUsers, onboardingTasks, qualityCheckRemarks, clientFeedback, attachments } = client;

    const prompt = `
    You are an expert SaaS Customer Success Manager.
    Your task is to draft a brief, professional note for an internal quality check call with a client.
    The note should synthesize all available information into a concise, actionable summary.

    Client Details:
    - Name: ${name}
    - Company: ${company}
    - Email: ${email}
    - Number of Users: ${numberOfUsers}
    - Financials: ${getFinancialSummary(client)}
    - Attachments: ${attachments.length} files on record (${attachments.map(a => a.type).join(', ')}).
    - Website Status: ${getServiceStatusText(onboardingTasks.hasWebsite, onboardingTasks.websiteDelivered)} (${getLatestRemarkText(onboardingTasks.websiteDelivered)})
    - Sales Funnel Status: ${getServiceStatusText(onboardingTasks.hasSalesFunnel, onboardingTasks.salesFunnelDelivered)} (${getLatestRemarkText(onboardingTasks.salesFunnelDelivered)})
    - Admin Training: ${getTaskStatusText(onboardingTasks.adminTrainingCompleted)} (${getLatestRemarkText(onboardingTasks.adminTrainingCompleted)})
    - Agent Training: ${getTaskStatusText(onboardingTasks.agentTrainingCompleted)} (${getLatestRemarkText(onboardingTasks.agentTrainingCompleted)})
    - Website Training: ${getTaskStatusText(onboardingTasks.websiteTrainingCompleted)} (${getLatestRemarkText(onboardingTasks.websiteTrainingCompleted)})
    - Recent Client Feedback: "${clientFeedback || 'None provided'}"
    - Previous Internal Remarks: "${qualityCheckRemarks || 'None'}"

    Instructions:
    1. Analyze the client's feedback for sentiment (positive, negative, or neutral).
    2. Review all delivery and training statuses, scheduled dates, and latest remarks for pending items or items awaiting approval.
    3. Consider financial status. A pending payment might be a point of friction to address.
    4. Based on the feedback and project status, draft a professional internal note for the "Quality Check Remarks" field.
    5. If feedback is negative or key items are not 'Completed', suggest specific follow-up actions (e.g., "Manager needs to approve reported agent training," "Confirm schedule for upcoming admin training").
    6. If feedback is positive and all onboarding is complete, suggest checking in for a potential case study or identifying upsell opportunities.
    7. If there's no feedback, focus on the project status and suggest proactive next steps to complete onboarding.
    8. The output must be a single paragraph suitable for a CRM notes field. Do not use markdown or headings.
    
    Generate the quality check call note.
    `;

    const response = await ai.models.generateContent({
      model: 'gemini-1.5-flash',
      contents: prompt,
    });

    let text = '';
    if ('text' in response && typeof response.text === 'string') {
      text = response.text;
    } else if ('response' in response && response.response && typeof (response.response as any).text === 'function') {
      text = await (response.response as any).text();
    }

    return text || 'Unable to generate suggestions at this time.';
  } catch (error: any) {
    return handleGeminiError(error);
  }
};

export async function generateSatisfactionAnalysis(client: any): Promise<{ satisfactionLevel: string, renewalPrediction: string }> {
  try {
    // Check rate limits before making request
    await checkRateLimit();
    
    const prompt = `
    You are an expert SaaS Customer Success Analyst. Given the following client details, comments, and feedback, analyze the client's satisfaction level and predict their likelihood to renew. Respond in JSON with keys: satisfactionLevel (one of: Very Satisfied, Satisfied, Neutral, Dissatisfied, Very Dissatisfied) and renewalPrediction (Likely to Renew, Uncertain, Unlikely to Renew).

    Client Details:
    ${JSON.stringify(client, null, 2)}
    `;

    const response = await ai.models.generateContent({
      model: 'gemini-1.5-flash',
      contents: prompt,
    });

    // Parse the AI response for JSON
    let satisfactionLevel = 'Neutral';
    let renewalPrediction = 'Uncertain';
    
    try {
      let text = '';
      if ('text' in response && typeof response.text === 'string') {
        text = response.text;
      } else if ('response' in response && response.response && typeof (response.response as any).text === 'function') {
        text = await (response.response as any).text();
      }
      
      const match = text.match(/\{[\s\S]*\}/);
      if (match) {
        const result = JSON.parse(match[0]);
        satisfactionLevel = result.satisfactionLevel || satisfactionLevel;
        renewalPrediction = result.renewalPrediction || renewalPrediction;
      }
    } catch (e) {
      // Fallback to defaults if parsing fails
      console.warn('Failed to parse AI response, using defaults');
    }
    
    return { satisfactionLevel, renewalPrediction };
  } catch (error: any) {
    const errorMessage = handleGeminiError(error);
    console.error('Satisfaction analysis error:', errorMessage);
    return { 
      satisfactionLevel: 'Neutral', 
      renewalPrediction: 'Uncertain' 
    };
  }
}

export async function generateBusinessAnalysis(
  question: string,
  dashboardData: {
    stats: any;
    revenueData: any[];
    serviceData: any[];
    topClients: any[];
    userPerformance: any[];
    systemHealth: any;
    businessIntelligence: any;
    dateRange: string;
    agentClientRelationships: any[];
    clientAgentMapping: any[];
    revenueByAgent: any[];
    agentPerformanceMetrics: any[];
    serviceAnalysis: any;
    kpiSummary: any;
    dataQuality: any;
  }
): Promise<string> {
  try {
    // Check rate limits before making request
    await checkRateLimit();
    
    const prompt = `
    You are an expert business intelligence analyst and CEO advisor. You have access to comprehensive business dashboard data including detailed agent-client relationships, transaction breakdowns, and performance metrics. Provide a concise, to-the-point analysis to answer the CEO's question.

    CEO Question: "${question}"

    COMPREHENSIVE DASHBOARD DATA:
    
    Basic Stats: ${JSON.stringify(dashboardData.stats, null, 2)}
    Revenue Data: ${JSON.stringify(dashboardData.revenueData, null, 2)}
    Service Data: ${JSON.stringify(dashboardData.serviceData, null, 2)}
    Top Clients: ${JSON.stringify(dashboardData.topClients, null, 2)}
    User Performance: ${JSON.stringify(dashboardData.userPerformance, null, 2)}
    System Health: ${JSON.stringify(dashboardData.systemHealth, null, 2)}
    Business Intelligence: ${JSON.stringify(dashboardData.businessIntelligence, null, 2)}
    
    ENHANCED ANALYSIS DATA:
    Agent-Client Relationships: ${JSON.stringify(dashboardData.agentClientRelationships, null, 2)}
    Client-Agent Mapping: ${JSON.stringify(dashboardData.clientAgentMapping, null, 2)}
    Revenue by Agent: ${JSON.stringify(dashboardData.revenueByAgent, null, 2)}
    Agent Performance Metrics: ${JSON.stringify(dashboardData.agentPerformanceMetrics, null, 2)}
    Service Analysis: ${JSON.stringify(dashboardData.serviceAnalysis, null, 2)}
    KPI Summary: ${JSON.stringify(dashboardData.kpiSummary, null, 2)}
    Data Quality: ${JSON.stringify(dashboardData.dataQuality, null, 2)}
    
    Time Period: ${dashboardData.dateRange}

    INSTRUCTIONS:
    1. **You now have comprehensive data** - No need to mention missing connections or data gaps
    2. **Agent-client relationships** are clearly defined in agentClientRelationships and clientAgentMapping
    3. **Revenue attribution** is available in revenueByAgent and agentPerformanceMetrics
    4. **Performance analysis** can be done using the enhanced metrics provided
    5. **Keep response concise** - 200 words or less, focus on key insights
    6. **Use specific numbers** from the provided data
    7. **Suggest visualizations** when helpful:
       - 📊 Bar charts for comparisons
       - 📈 Line graphs for trends  
       - 🥧 Pie charts for distributions
       - 📉 Performance dashboards
    8. **Format with markdown**: Use **bold** for headings, bullet points for lists
    9. **Provide actionable insights** - what should the CEO do next?
    
    Provide your analysis using the comprehensive data provided:
    `;

    const response = await ai.models.generateContent({
      model: 'gemini-1.5-flash',
      contents: prompt,
    });

    let text = '';
    if ('text' in response && typeof response.text === 'string') {
      text = response.text;
    } else if ('response' in response && response.response && typeof (response.response as any).text === 'function') {
      text = await (response.response as any).text();
    }

    return text || 'Unable to generate business analysis at this time.';
  } catch (error: any) {
    return handleGeminiError(error);
  }
}

// Export quota status for monitoring
export const getQuotaStatus = () => ({
  dailyRequestCount,
  dailyLimit: RATE_LIMIT_CONFIG.maxRequestsPerDay,
  requestsRemaining: Math.max(0, RATE_LIMIT_CONFIG.maxRequestsPerDay - dailyRequestCount),
  lastRequestTime,
  nextResetTime: dailyResetTime
});

// Export helper functions for use in other AI services
export { checkRateLimit, handleGeminiError };